import { array, number, ord, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { filter, get, includes, isNil, some } from 'lodash/fp';
import { createSelector } from 'reselect';

import { isRelativeProductPricingCoProduct } from '@mews-distributor/common';
import { getCollectionIds, getStringProp, values } from '@mews/fp';

import { getLanguageCode } from '../../app/selectors';
import { getProductIds, getSelectedHotelId } from '../../resourceOrder/selectors';
import { getEntities } from '../getEntities';
import { Product, Products } from './types';

const getProductsSlice = createSelector(getEntities, (entities) => entities.products);
const getProducts = createSelector(getProductsSlice, (entities) => entities.data);

export const getProduct = createSelector(getProducts, getStringProp('id'), (products, id) => products[id]);
export const getProductName = flow(getProduct, get('name'));
export const getProductEnterpriseId = createSelector(getProduct, get('enterpriseId'));

const byOrdering = ord.contramap(({ ordering }: Product) => ordering)(number.Ord);
const byName = (languageCode: string) =>
    ord.contramap(({ name }: Product) =>
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        name && values(name).length > 0
            ? string.toLowerCase(name[languageCode] ? name[languageCode] : values(name)[0] ?? 'z')
            : 'z'
    )(string.Ord);

const filterEnterpriseProducts = (enterpriseId: string, products: Products, languageCode = 'en-US') =>
    pipe(
        products,
        values,
        array.filter((product) => product.enterpriseId === enterpriseId),
        array.sortBy([byOrdering, byName(languageCode)])
    );

const getSelectedEnterpriseProducts = createSelector(
    getSelectedHotelId,
    getProducts,
    getLanguageCode,
    filterEnterpriseProducts
);

const getEnterpriseProducts = createSelector(
    getStringProp('enterpriseId'),
    getProducts,
    getLanguageCode,
    filterEnterpriseProducts
);

const getEnterpriseAbsoluteProducts = createSelector(
    getEnterpriseProducts,
    array.filter(({ pricing }) => !isRelativeProductPricingCoProduct(pricing))
);
export const getEnterpriseAbsoluteProductIds = flow(getEnterpriseAbsoluteProducts, getCollectionIds);

export const doesSelectedEnterpriseHaveProducts = createSelector(getSelectedEnterpriseProducts, array.isNonEmpty);

export const getSelectedEnterpriseProductIds = createSelector(getSelectedEnterpriseProducts, getCollectionIds);

export const doesAnyProductHaveImage = createSelector(
    getSelectedEnterpriseProducts,
    some((product) => !isNil(product.imageId))
);

export const isProductSelected = createSelector(getStringProp('productId'), getProductIds, includes);

const isBasicProduct = (product: Product) => product.includedByDefault || product.alwaysIncluded;
const getBasicProductIds = flow(filter(isBasicProduct), getCollectionIds);

export const getEnterpriseBasicProductIds = createSelector(getEnterpriseProducts, getBasicProductIds);

export const getSelectedEnterpriseBasicProductIds = createSelector(getSelectedEnterpriseProducts, getBasicProductIds);
